import { ListViewControls } from "@/components/tables/ListViewControls";
import type { ListView } from "@/hooks/useListView";
import { AlertTriangle, Pencil, Trash2 } from "lucide-react";
import { Button, DataTable, Input, Mono, Text } from "@athena/ui";
import type { CrisisResourcesQuery } from "../../graphql/crisis.generated";

type CrisisResource = CrisisResourcesQuery["crisisResources"][number];

interface CrisisResourcesProps {
    search: string;
    onSearchChange: (value: string) => void;
    view: ListView<CrisisResource>;
    resultsLabel: string;
    selected: Set<string>;
    onEdit: (resource: CrisisResource) => void;
    onDelete: (resource: CrisisResource) => void;
}

export function CrisisResources({
    search,
    onSearchChange,
    view,
    resultsLabel,
    selected,
    onEdit,
    onDelete,
}: CrisisResourcesProps) {
    return (
        <>
            {/* Filters */}
            <div className="mb-4 flex items-center gap-3">
                <div className="relative max-w-xs flex-1">
                    <AlertTriangle className="text-muted-foreground absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
                    <Input
                        placeholder="Search resources..."
                        className="bg-card h-9 pl-9 text-sm"
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                    />
                </div>
            </div>

            {/* Table */}
            {/* Above the table, where Becca asked for it: "the top of the table row or above it". */}
            <ListViewControls view={view} noun="resources" count={resultsLabel} />

            <DataTable
                columns={[
                    {
                        key: "message",
                        header: "Message",
                        cell: (resource) => (
                            <Text weight="medium" className="text-sm">
                                {resource.message}
                            </Text>
                        ),
                    },
                    {
                        key: "email",
                        header: "Email",
                        cell: (resource) => (
                            <Mono size="xs" muted>
                                {resource.email ?? "—"}
                            </Mono>
                        ),
                    },
                    {
                        key: "phone",
                        header: "Phone",
                        cell: (resource) => (
                            <Mono size="xs" muted>
                                {resource.phoneNumber ?? "—"}
                            </Mono>
                        ),
                    },
                ]}
                rows={view.rows}
                rowKey={(resource) => resource.id}
                rowSelected={(resource) => selected.has(resource.id)}
                filtered={!!search}
                empty={
                    <div className="py-12 text-center">
                        <Text muted className="text-sm">
                            No resources yet. Add one now!
                        </Text>
                    </div>
                }
                noMatches="No resources match your filters."
                actions={(resource) => (
                    <div className="flex items-center justify-end gap-1">
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-foreground"
                            aria-label={`Edit ${resource.message}`}
                            onClick={() => onEdit(resource)}
                        >
                            <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-destructive"
                            aria-label={`Delete ${resource.message}`}
                            onClick={() => onDelete(resource)}
                        >
                            <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )}
            />

            <div className="mt-4">
                <Mono className="text-muted-foreground text-xs">
                    {selected.size > 0 ? `${selected.size} selected` : ""}
                </Mono>
            </div>
        </>
    );
}
